import { Check, Copy } from 'lucide-react';
import { useCallback, useState } from 'react';
import { cn } from '@/lib/classnames';

export function CopyableId({
  id,
  className,
  truncate = true,
  prefix = ''
}: {
  id: string | undefined | null;
  className?: string;
  truncate?: boolean;
  prefix?: string;
}) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback((e: React.MouseEvent | React.KeyboardEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!id) return;
    navigator.clipboard.writeText(id).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {
      // clipboard unavailable or denied - no false success
    });
  }, [id]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      handleCopy(e);
    }
  }, [handleCopy]);

  if (!id) return null;

  const displayId = truncate && id.length > 18 ? id.slice(0, 18) : id;

  return (
    <span
      role="button"
      tabIndex={0}
      onClick={handleCopy}
      onKeyDown={handleKeyDown}
      aria-label={`Copy ID ${id}`}
      className={cn(
        "group ring-focus inline-flex items-center gap-1.5 px-1.5 -ml-1.5 py-0.5 rounded cursor-pointer transition-colors motion-reduce:transition-none",
        "font-mono text-ink-400 hover:text-ink-50 hover:bg-ink-850",
        copied && "text-verdict-clean hover:text-verdict-clean",
        className
      )}
      title="Click to copy ID"
    >
      <span>{prefix}{displayId}</span>
      {copied ? (
        <Check className="w-3 h-3 opacity-100 shrink-0" />
      ) : (
        <Copy className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity motion-reduce:transition-none shrink-0" />
      )}
    </span>
  );
}
